import { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { CALENDAR } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Ban, Flame, Mail, Phone, Bell } from "lucide-react";
import { toast } from "sonner";
import { useSectionTheme } from "@/hooks/useSectionTheme";

const SOURCE_TONES = {
  airbnb: { bg: "bg-rose-500/90", label: "Airbnb" },
  booking: { bg: "bg-blue-600/90", label: "Booking.com" },
  vrbo: { bg: "bg-amber-500/90", label: "Vrbo" },
  expedia: { bg: "bg-yellow-500/90", label: "Expedia" },
  internal: { bg: "bg-teal-700/90", label: "Direct" },
  hostaway: { bg: "bg-orange-500/90", label: "Hostaway" },
};

const toneFor = (b) => {
  const src = (b.source || b.channel || "").toLowerCase();
  if (SOURCE_TONES[src]) return SOURCE_TONES[src];
  if (b.hostaway_id) return SOURCE_TONES.hostaway;
  return SOURCE_TONES.internal;
};

const fmtDay = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return { dow: d.toLocaleDateString(undefined, { weekday: "short" }).slice(0, 2), num: d.getDate() };
};

function HotPillPopover({ propertyId, watchers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    if (data) return;
    setLoading(true);
    api
      .get(`/wishlist/watchers/${propertyId}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ watchers: [], count: 0 }))
      .finally(() => setLoading(false));
  };

  const exportCsv = async () => {
    try {
      const resp = await api.get(`/wishlist/watchers/${propertyId}?format=csv`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const match = /filename="([^"]+)"/.exec(resp.headers?.["content-disposition"] || "");
      a.download = match ? match[1] : `watchers_${propertyId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export CSV");
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={`calendar-hot-${propertyId}`}
          onClick={load}
          onMouseEnter={load}
          className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-medium px-2 py-0.5 hover:bg-amber-200 transition"
        >
          <Flame className="w-3 h-3" /> Hot · {watchers}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-0"
        data-testid={`calendar-hot-popover-${propertyId}`}
      >
        <div className="p-3 border-b border-slate-100 flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="ns-label text-slate-400">Who's wishlisting this unit?</div>
            <div className="text-xs text-slate-500 mt-1">
              {loading
                ? "Loading…"
                : data?.count
                  ? `${data.count} guest${data.count === 1 ? "" : "s"} watching · consider bumping the rate`
                  : "No one watching right now"}
            </div>
          </div>
          {data?.count > 0 && (
            <button
              type="button"
              onClick={exportCsv}
              data-testid={`calendar-hot-export-${propertyId}`}
              className="text-[10px] rounded-full bg-teal-700 text-white px-2.5 py-1 hover:bg-teal-800 transition shrink-0"
              title="Download watchers as CSV"
            >
              Export CSV
            </button>
          )}
        </div>
        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
          {(data?.watchers || []).map((w) => {
            const fullName = `${w.first_name} ${w.last_name}`.trim() || w.email || "Guest";
            const subject = encodeURIComponent(
              `Newport Stays — a window just opened at ${data.property_name}`,
            );
            return (
              <div
                key={w.contact_id}
                data-testid={`calendar-hot-watcher-${w.contact_id}`}
                className="p-3 hover:bg-stone-50"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="font-medium text-sm truncate">{fullName}</div>
                  {w.alert_frequency === "weekly" && (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-teal-50 text-teal-700 text-[9px] font-medium px-1.5 py-0.5">
                      <Bell className="w-2.5 h-2.5" /> weekly
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500 truncate">
                  <Mail className="w-3 h-3 shrink-0" />
                  <span className="truncate">{w.email || "—"}</span>
                </div>
                {w.phone && (
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 truncate">
                    <Phone className="w-3 h-3 shrink-0" /> {w.phone}
                  </div>
                )}
                <div className="mt-2 flex gap-2">
                  {w.email && (
                    <a
                      href={`mailto:${w.email}?subject=${subject}`}
                      className="text-[11px] rounded-full bg-teal-50 text-teal-800 px-2.5 py-1 hover:bg-teal-100"
                      data-testid={`calendar-hot-email-${w.contact_id}`}
                    >
                      Email
                    </a>
                  )}
                  {w.phone && (
                    <a
                      href={`sms:${w.phone}`}
                      className="text-[11px] rounded-full bg-stone-100 text-slate-700 px-2.5 py-1 hover:bg-stone-200"
                      data-testid={`calendar-hot-sms-${w.contact_id}`}
                    >
                      SMS
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Calendar() {
  const sectionTheme = useSectionTheme("calendar");
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  const highlightDate = searchParams.get("date");
  const [start, setStart] = useState(() =>
    (highlightDate && /^\d{4}-\d{2}-\d{2}$/.test(highlightDate))
      ? highlightDate
      : new Date().toISOString().slice(0, 10),
  );
  const [days] = useState(21);
  const [grid, setGrid] = useState(null);
  const [demand, setDemand] = useState({});
  const [editor, setEditor] = useState(null); // { property, date, existing }
  const highlightRef = useRef(null);
  const [highlightPulse, setHighlightPulse] = useState(false);

  const load = () => {
    api
      .get("/calendar/grid", { params: { start, days } })
      .then((r) => setGrid(r.data))
      .catch((err) => toast.error(apiError(err)));
    api
      .get("/wishlist/demand")
      .then((r) => setDemand(r.data || {}))
      .catch(() => setDemand({}));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start]);

  // Session Z22 — Dashboard drill-in: scroll the highlighted row into view
  // and flash it for a couple of seconds so the owner immediately spots it.
  useEffect(() => {
    if (!grid || !highlightId) return;
    if (!highlightRef.current) return;
    highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightPulse(true);
    const t = setTimeout(() => {
      setHighlightPulse(false);
      const next = new URLSearchParams(searchParams);
      next.delete("highlight");
      next.delete("date");
      setSearchParams(next, { replace: true });
    }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid, highlightId]);

  const shift = (delta) => {
    const d = new Date(start + "T00:00:00");
    d.setDate(d.getDate() + delta);
    setStart(d.toISOString().slice(0, 10));
  };

  // Precompute a lookup { propId: { date: [bookings] } }
  const bookingByCell = useMemo(() => {
    if (!grid) return {};
    const m = {};
    for (const b of grid.bookings || []) {
      const ci = b.check_in;
      const co = b.check_out;
      if (!ci || !co) continue;
      let cur = new Date(ci + "T00:00:00");
      const end = new Date(co + "T00:00:00");
      while (cur < end) {
        const iso = cur.toISOString().slice(0, 10);
        if (grid.dates.includes(iso)) {
          m[b.property_id] = m[b.property_id] || {};
          m[b.property_id][iso] = m[b.property_id][iso] || [];
          m[b.property_id][iso].push({
            ...b,
            _isCheckIn: iso === ci,
            _isCheckOut: (() => {
              const nxt = new Date(cur);
              nxt.setDate(nxt.getDate() + 1);
              return nxt.toISOString().slice(0, 10) === co;
            })(),
          });
        }
        cur.setDate(cur.getDate() + 1);
      }
    }
    return m;
  }, [grid]);

  const availByCell = useMemo(() => {
    if (!grid) return {};
    const m = {};
    for (const a of grid.availability || []) {
      m[a.property_id] = m[a.property_id] || {};
      m[a.property_id][a.date] = a;
    }
    return m;
  }, [grid]);

  const openEditor = (property, date) => {
    const existing = availByCell[property.id]?.[date] || null;
    setEditor({
      property,
      date,
      rate: existing?.rate ?? "",
      min_nights: existing?.min_nights ?? "",
      blocked: !!existing?.blocked,
    });
  };

  const saveEditor = async () => {
    try {
      await api.put(`/availability/${editor.property.id}/${editor.date}`, {
        rate: editor.rate === "" ? null : Number(editor.rate),
        min_nights: editor.min_nights === "" ? null : Number(editor.min_nights),
        blocked: editor.blocked,
      });
      toast.success(`${editor.property.name} · ${editor.date} saved`);
      setEditor(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!grid) {
    return (
      <Layout title="Calendar" subtitle="Loading unified view…">
        <div className="ns-card p-10 text-center text-slate-400">Loading…</div>
      </Layout>
    );
  }

  return (
    <Layout
      title="Calendar"
      subtitle="Every unit, every channel. Click any night to set rates, minimums, or block dates."
      actions={
        <>
          <Button
            variant="outline"
            data-testid={CALENDAR.prevBtn}
            onClick={() => shift(-7)}
            className="rounded-full h-11 w-11 p-0"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            data-testid={CALENDAR.todayBtn}
            variant="outline"
            onClick={() => setStart(new Date().toISOString().slice(0, 10))}
            className="rounded-full h-11 px-5"
          >
            Today
          </Button>
          <Button
            variant="outline"
            data-testid={CALENDAR.nextBtn}
            onClick={() => shift(7)}
            className="rounded-full h-11 w-11 p-0"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </>
      }
    >
      <section className={`ns-section ns-section-${sectionTheme} mb-5`} data-section-theme={sectionTheme}>
        <div className="flex items-start gap-3">
          <div className={`ns-rail ns-rail-${sectionTheme} self-stretch min-h-[2.5rem]`} />
          <div className="flex-1 min-w-0">
            <div className="ns-label text-indigo-800">Channel legend</div>
            <div className="font-serif text-lg text-slate-900 mt-0.5">Which colour is which OTA</div>
            <div className="flex gap-3 flex-wrap mt-3">
              {Object.entries(SOURCE_TONES).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 text-xs text-slate-600 bg-white rounded-full border border-slate-200 px-3 py-1">
                  <span className={`inline-block w-3 h-3 rounded ${v.bg}`} />
                  <span className="font-medium">{v.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div data-testid={CALENDAR.root} className="ns-card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-white ns-label text-left px-4 py-3 w-56 border-b border-slate-100">
                Unit
              </th>
              {grid.dates.map((d) => {
                const { dow, num } = fmtDay(d);
                const isToday = d === new Date().toISOString().slice(0, 10);
                return (
                  <th
                    key={d}
                    className={`px-1 py-2 border-b border-slate-100 text-center min-w-[44px] ${
                      isToday ? "bg-teal-50/60" : ""
                    }`}
                  >
                    <div className="text-[10px] tracking-wider uppercase text-slate-400">
                      {dow}
                    </div>
                    <div className={`font-serif text-lg ${isToday ? "text-teal-800" : "text-slate-700"}`}>
                      {num}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {grid.properties.map((p) => {
              const dem = demand[p.id];
              const hot = dem?.hot;
              const isHighlight = highlightId === p.id;
              return (
              <tr
                key={p.id}
                ref={isHighlight ? highlightRef : null}
                data-testid={isHighlight ? "calendar-highlight-row" : undefined}
                className={`border-b border-slate-100 transition-colors ${
                  isHighlight && highlightPulse ? "bg-amber-50/70 ring-1 ring-inset ring-amber-200" : ""
                }`}
              >
                <td className="sticky left-0 z-10 bg-white px-4 py-3 w-56 border-r border-slate-100">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium truncate">{p.name}</span>
                    {hot && (
                      <HotPillPopover propertyId={p.id} watchers={dem.watchers} />
                    )}
                  </div>
                  <div className="text-[11px] text-slate-400">{p.bedrooms} bed</div>
                </td>
                {grid.dates.map((d) => {
                  const cell = bookingByCell[p.id]?.[d];
                  const av = availByCell[p.id]?.[d];
                  const b = cell?.[0];
                  const tone = b ? toneFor(b) : null;
                  return (
                    <td
                      key={d}
                      data-testid={CALENDAR.cell(p.id, d)}
                      onClick={() => openEditor(p, d)}
                      className={`p-0 border-r border-slate-50 last:border-r-0 relative align-middle h-14 cursor-pointer group hover:bg-teal-50/40 ${
                        av?.blocked ? "bg-slate-100" : ""
                      }`}
                    >
                      {b ? (
                        <div
                          className={`h-8 mx-0.5 my-3 ${tone.bg} text-white text-[10px] flex items-center px-2 truncate ${
                            b._isCheckIn ? "rounded-l-full ml-2" : ""
                          } ${b._isCheckOut ? "rounded-r-full mr-2" : ""}`}
                          title={`${b.guest || ""} · ${b.check_in} → ${b.check_out}`}
                        >
                          {b._isCheckIn && (
                            <span className="truncate">{b.guest}</span>
                          )}
                        </div>
                      ) : av?.blocked ? (
                        <Ban className="w-3.5 h-3.5 text-slate-400 mx-auto" />
                      ) : av?.rate ? (
                        <div className="text-[10px] text-slate-500 text-center">
                          £{Math.round(av.rate)}
                        </div>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editor?.property?.name}
            </DialogTitle>
            <div className="ns-label mt-1">{editor?.date}</div>
          </DialogHeader>
          {editor && (
            <div className="space-y-4">
              <div>
                <Label className="ns-label">Nightly rate (£)</Label>
                <Input
                  data-testid={CALENDAR.editorRate}
                  type="number"
                  min={0}
                  className="mt-2 h-11 bg-white"
                  value={editor.rate}
                  onChange={(e) => setEditor({ ...editor, rate: e.target.value })}
                />
              </div>
              <div>
                <Label className="ns-label">Minimum nights</Label>
                <Input
                  data-testid={CALENDAR.editorMin}
                  type="number"
                  min={1}
                  className="mt-2 h-11 bg-white"
                  value={editor.min_nights}
                  onChange={(e) => setEditor({ ...editor, min_nights: e.target.value })}
                />
              </div>
              <label className="flex items-center gap-3 text-sm text-slate-700 select-none cursor-pointer">
                <input
                  data-testid={CALENDAR.editorBlocked}
                  type="checkbox"
                  checked={editor.blocked}
                  onChange={(e) => setEditor({ ...editor, blocked: e.target.checked })}
                  className="w-4 h-4 accent-teal-600"
                />
                <span>Block this night (no bookings, no direct enquiries)</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditor(null)}>
              Cancel
            </Button>
            <Button
              data-testid={CALENDAR.editorSave}
              onClick={saveEditor}
              className="rounded-full ns-btn-primary px-6"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
